/**
 * Personnel (08 §2.10, 04 §5): everyone this profile has ever known, and one man's file at a time.
 *
 * The other roster screens each answer a question of the moment - who can I send, who do I induct,
 * who have I lost. This one answers the one nothing else did: who IS this man, and what has he
 * actually done? Pick him off the list and you get his dossier and his service file, mission by
 * mission (`widgets/serviceFile.ts`).
 *
 * The screen holds the selected ranger and nothing else; everything shown is read from the profile
 * on the way in, as every screen here does.
 */

import './rosters.css';
import type { RangerRecord } from '../../meta/types';
import type { Ctx, Screen } from './context';
import { box, button, el } from './dom';
import { fullName, rankAbbr, serviceName } from './text';
import { choiceRow } from './widgets/choiceRow';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';
import { serviceFile } from './widgets/serviceFile';
import { stamp } from './widgets/stamp';

/** Which part of the company is on screen. */
export type Posting = 'serving' | 'hall' | 'retired' | 'fallen';

export interface Personnel {
  ranger: RangerRecord;
  posting: Posting;
  /** The fallen carry how and where they were lost; nobody else has one. */
  fell?: { fate: 'kia' | 'lost'; operation: string; mission: string; cause: string; epitaph: string };
}

/**
 * Killed, or a prisoner nobody ever brought home. The wall has always told these two apart and so
 * must this: a man taken alive was not killed, and his file should not say he was.
 */
const FATE_STAMP = { kia: 'KIA', lost: 'LOST' } as const;
const FATE_WORD = { kia: 'KILLED', lost: 'NEVER RECOVERED' } as const;

const POSTING_LABEL: Record<Posting, string> = {
  serving: 'SERVING',
  hall: 'HALL OF HONOR',
  retired: 'RETIRED',
  fallen: 'FALLEN',
};

const EMPTY: Record<Posting, string> = {
  serving: 'NO OPERATION IS UNDER WAY. START ONE AND YOUR COMPANY WILL BE HERE.',
  hall: 'NO VETERANS YET. WIN AN OPERATION AND ONE SURVIVOR IS INDUCTED.',
  retired: 'NOBODY HAS RETIRED YET.',
  fallen: 'NOBODY HAS BEEN LOST. KEEP IT THAT WAY.',
};

/**
 * Everyone the profile knows, in the order a player thinks of them: the men in the field now, then
 * the Hall, then those who have stood down, then the dead. A ranger appears once, under the posting
 * he holds today. Pure - and the Classic roster rides with the serving, being its own small company.
 */
export function everyone(profile: ReturnType<Ctx['api']['profile']>): Personnel[] {
  const out: Personnel[] = [];
  for (const r of profile.operation?.company ?? []) out.push({ ranger: r, posting: 'serving' });
  for (const r of profile.classicRoster) if (r.status !== 'kia' && r.status !== 'lost') {
    out.push({ ranger: r, posting: 'serving' });
  }
  for (const r of profile.hall) out.push({ ranger: r, posting: 'hall' });
  for (const r of profile.retired) out.push({ ranger: r, posting: 'retired' });
  for (const entry of profile.memorial) {
    out.push({
      ranger: entry.ranger,
      posting: 'fallen',
      fell: {
        fate: entry.fate, operation: entry.operation, mission: entry.mission,
        cause: entry.cause, epitaph: entry.epitaph,
      },
    });
  }
  return out;
}

/** Those of one posting. Pure. */
export const posted = (all: readonly Personnel[], posting: Posting): Personnel[] =>
  all.filter((p) => p.posting === posting);

/** A posting with nobody in it is not offered: the row would be a button that shows an apology. */
export function postingsWithMen(all: readonly Personnel[]): Posting[] {
  const order: Posting[] = ['serving', 'hall', 'retired', 'fallen'];
  return order.filter((posting) => posted(all, posting).length > 0);
}

function summaryRow(entry: Personnel, open: () => void): HTMLElement {
  const r = entry.ranger;
  const row = button('', open, 'menu-person');
  // Name, then the callsign pushed right, then the stamp after it: a fallen man's KIA must not
  // land on top of his callsign, which is what an absolutely-placed stamp did.
  const name = box('menu-person-name', el('span', 'menu-paper', `${rankAbbr(r.rank)} ${fullName(r)}`));
  if (r.callsign.trim() !== '') name.append(el('span', 'menu-dim menu-person-callsign', `"${r.callsign}"`));
  if (entry.fell) name.append(stamp({ text: FATE_STAMP[entry.fell.fate], tone: 'red' }, true));
  row.append(
    name,
    box(
      'menu-person-line',
      el('span', 'menu-dim', `${r.record.missions} MSN - ${r.record.kills} KILLS`),
      r.medals.length > 0 ? el('span', 'menu-dim', `${r.medals.length} AWARD${r.medals.length === 1 ? '' : 'S'}`)
        : el('span', ''),
    ),
  );
  return row;
}

export function personnelScreen(ctx: Ctx): Screen {
  const profile = ctx.api.profile();
  const all = everyone(profile);
  const files = profile.flights;
  const f = frame('personnel', 'PERSONNEL');
  const count = el('span', '');
  f.info.append(count);

  const body = el('div', 'menu-col menu-grow');
  const back = button('BACK', () => ctx.nav.title());

  /** One man's file: his dossier, how he was lost if he was, and every mission he flew. */
  const showOne = (entry: Personnel): void => {
    const r = entry.ranger;
    count.textContent = serviceName(r);
    const card = dossierCard(r, { describe: ctx.describe, ...(entry.posting === 'fallen' ? { memorial: true } : {}) });
    const page = el('div', 'menu-col menu-scroll menu-grow');
    page.append(card);
    if (entry.fell) {
      const fell = el('div', 'menu-person-fell');
      fell.append(
        box(
          'menu-file-head',
          stamp({ text: FATE_WORD[entry.fell.fate], tone: 'red' }, true),
          el('span', '', entry.fell.mission),
        ),
        el('div', 'menu-file-line', `OPERATION ${entry.fell.operation}`),
        el('div', 'menu-file-result', entry.fell.cause),
        el('div', 'menu-prose menu-dim', entry.fell.epitaph),
      );
      page.append(fell);
    }
    page.append(serviceFile(files[r.id] ?? [], ctx.describe, r.record.missions));
    const toList = button('BACK TO THE LIST', () => showList(entry.posting), 'menu-btn-small');
    body.replaceChildren(page, box('menu-row menu-spread', toList, back));
    toList.focus();
  };

  /** Everyone of one posting, as one line each. */
  const showList = (posting: Posting): void => {
    const men = posted(all, posting);
    count.textContent = `${men.length} ${POSTING_LABEL[posting]}`;
    const list = el('div', 'menu-person-list menu-scroll menu-grow');
    for (const entry of men) list.append(summaryRow(entry, () => showOne(entry)));
    if (men.length === 0) list.append(el('div', 'menu-empty menu-prose', EMPTY[posting]));

    const choices = postingsWithMen(all).map((p) => ({ value: p, label: POSTING_LABEL[p] }));
    const row = choiceRow<Posting>('POSTING', choices, posting, showList);
    body.replaceChildren(box('menu-row menu-spread', row.el, back), list);
  };

  showList(postingsWithMen(all)[0] ?? 'serving');
  f.body.append(body);
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => back };
}
